import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// ============================================================================
// Schema
// ============================================================================

const financialAssetFormSchema = z.object({
  financialAssetType: z.enum([
    "inventory",
    "debtors",
    "term_deposit",
    "shares_listed",
    "shares_unlisted",
    "managed_funds",
    "bonds",
    "other_financial",
  ]),
  description: z.string().nullable().optional(),
  // Inventory
  inventoryType: z.string().nullable().optional(),
  inventoryLocation: z.string().nullable().optional(),
  // Debtors
  debtorAging30: z.string().nullable().optional(),
  debtorAging60: z.string().nullable().optional(),
  debtorAging90Plus: z.string().nullable().optional(),
  concentrationTopDebtorPercent: z.string().nullable().optional(),
  // Institution / shares / funds
  institutionName: z.string().nullable().optional(),
  accountNumber: z.string().nullable().optional(),
  numberOfUnits: z.string().nullable().optional(),
  unitPrice: z.string().nullable().optional(),
  asxCode: z.string().nullable().optional(),
  // Term deposit / bonds
  maturityDate: z.string().nullable().optional(),
  interestRate: z.string().nullable().optional(),
});

type FinancialAssetFormData = z.infer<typeof financialAssetFormSchema>;

// ============================================================================
// Types
// ============================================================================

type FinancialAssetType = FinancialAssetFormData["financialAssetType"];

interface FinancialAssetFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  securityId: string;
  existingAsset?: any | null;
}

// ============================================================================
// Constants
// ============================================================================

const FINANCIAL_ASSET_TYPE_OPTIONS: { value: FinancialAssetType; label: string }[] = [
  { value: "inventory", label: "Inventory" },
  { value: "debtors", label: "Debtors" },
  { value: "term_deposit", label: "Term Deposit" },
  { value: "shares_listed", label: "Shares (Listed)" },
  { value: "shares_unlisted", label: "Shares (Unlisted)" },
  { value: "managed_funds", label: "Managed Funds" },
  { value: "bonds", label: "Bonds" },
  { value: "other_financial", label: "Other Financial Asset" },
];

const defaultValues: FinancialAssetFormData = {
  financialAssetType: "term_deposit",
  description: null,
  inventoryType: null,
  inventoryLocation: null,
  debtorAging30: null,
  debtorAging60: null,
  debtorAging90Plus: null,
  concentrationTopDebtorPercent: null,
  institutionName: null,
  accountNumber: null,
  numberOfUnits: null,
  unitPrice: null,
  asxCode: null,
  maturityDate: null,
  interestRate: null,
};

// ============================================================================
// Helper — normalise existing asset for form
// ============================================================================

function normalizeForForm(asset: any): FinancialAssetFormData {
  return {
    financialAssetType: asset.financialAssetType ?? "term_deposit",
    description: asset.description ?? null,
    inventoryType: asset.inventoryType ?? null,
    inventoryLocation: asset.inventoryLocation ?? null,
    debtorAging30: asset.debtorAging30 ?? null,
    debtorAging60: asset.debtorAging60 ?? null,
    debtorAging90Plus: asset.debtorAging90Plus ?? null,
    concentrationTopDebtorPercent: asset.concentrationTopDebtorPercent ?? null,
    institutionName: asset.institutionName ?? null,
    accountNumber: asset.accountNumber ?? null,
    numberOfUnits: asset.numberOfUnits ?? null,
    unitPrice: asset.unitPrice ?? null,
    asxCode: asset.asxCode ?? null,
    maturityDate: asset.maturityDate ?? null,
    interestRate: asset.interestRate ?? null,
  };
}

// ============================================================================
// Calculated total display
// ============================================================================

function calcTotal(units: string | null | undefined, price: string | null | undefined): string {
  const u = parseFloat(units || "0");
  const p = parseFloat(price || "0");
  if (isNaN(u) || isNaN(p)) return "-";
  const total = u * p;
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(total);
}

// ============================================================================
// Main component
// ============================================================================

export function FinancialAssetFormModal({
  open,
  onOpenChange,
  securityId,
  existingAsset,
}: FinancialAssetFormModalProps) {
  const isEdit = !!existingAsset;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<FinancialAssetFormData>({
    resolver: zodResolver(financialAssetFormSchema),
    defaultValues,
  });

  const watchedType = form.watch("financialAssetType");
  const watchedUnits = form.watch("numberOfUnits");
  const watchedPrice = form.watch("unitPrice");

  // Reset / populate form when modal opens
  useEffect(() => {
    if (open) {
      if (existingAsset) {
        form.reset(normalizeForForm(existingAsset));
      } else {
        form.reset(defaultValues);
      }
    }
  }, [open, existingAsset, form]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset(defaultValues);
    }
    onOpenChange(newOpen);
  };

  // ---- Mutations ----

  const createMutation = useMutation({
    mutationFn: async (data: FinancialAssetFormData) => {
      const response = await apiRequest(
        "POST",
        `/api/crm/securities/${securityId}/financial-asset`,
        data
      );
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || "Failed to create financial asset");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/crm/securities", securityId, "financial-asset"],
      });
      toast({ title: "Financial asset created" });
      handleOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: FinancialAssetFormData) => {
      const response = await apiRequest(
        "PATCH",
        `/api/crm/securities/${securityId}/financial-asset`,
        data
      );
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || "Failed to update financial asset");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/crm/securities", securityId, "financial-asset"],
      });
      toast({ title: "Financial asset updated" });
      handleOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const onSubmit = (data: FinancialAssetFormData) => {
    // Convert empty strings to null for API
    const payload: FinancialAssetFormData = {
      ...data,
      description: data.description || null,
      inventoryType: data.inventoryType || null,
      inventoryLocation: data.inventoryLocation || null,
      debtorAging30: data.debtorAging30 || null,
      debtorAging60: data.debtorAging60 || null,
      debtorAging90Plus: data.debtorAging90Plus || null,
      concentrationTopDebtorPercent: data.concentrationTopDebtorPercent || null,
      institutionName: data.institutionName || null,
      accountNumber: data.accountNumber || null,
      numberOfUnits: data.numberOfUnits || null,
      unitPrice: data.unitPrice || null,
      asxCode: data.asxCode || null,
      maturityDate: data.maturityDate || null,
      interestRate: data.interestRate || null,
    };
    if (isEdit) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  // ============================================================================
  // Field groups by type
  // ============================================================================

  const showInventory = watchedType === "inventory";
  const showDebtors = watchedType === "debtors";
  const showSharesListed = watchedType === "shares_listed";
  const showSharesUnlisted = watchedType === "shares_unlisted";
  const showShares = showSharesListed || showSharesUnlisted;
  const showManagedFunds = watchedType === "managed_funds";
  const showTermDeposit = watchedType === "term_deposit";
  const showBonds = watchedType === "bonds";
  const showOther = watchedType === "other_financial";

  // Institution + account group shown for: term_deposit, bonds, shares, managed_funds
  const showInstitution = showTermDeposit || showBonds || showShares || showManagedFunds;
  // Account number shown for: term_deposit, bonds, managed_funds (not shares)
  const showAccountNumber = showTermDeposit || showBonds || showManagedFunds;
  // Maturity date / interest rate shown for: term_deposit, bonds
  const showMaturityRate = showTermDeposit || showBonds;
  // Units + price shown for: shares, managed_funds
  const showUnitsPrice = showShares || showManagedFunds;
  // ASX code shown for listed shares only
  const showAsxCode = showSharesListed;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Financial Asset" : "Add Financial Asset"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Financial Asset Type — always shown */}
            <FormField
              control={form.control}
              name="financialAssetType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Asset Type *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="select-financial-asset-type">
                        <SelectValue placeholder="Select asset type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {FINANCIAL_ASSET_TYPE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* ── INVENTORY fields ── */}
            {showInventory && (
              <div className="space-y-4 border rounded-md p-4">
                <p className="text-sm font-medium text-muted-foreground">Inventory Details</p>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="inventoryType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Inventory Type</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. Raw materials"
                            data-testid="input-inventory-type"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="inventoryLocation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Location</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. Warehouse A"
                            data-testid="input-inventory-location"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            )}

            {/* ── DEBTORS fields ── */}
            {showDebtors && (
              <div className="space-y-4 border rounded-md p-4">
                <p className="text-sm font-medium text-muted-foreground">Debtor Aging</p>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="debtorAging30"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>0–30 Days ($)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            data-testid="input-debtor-aging-30"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="debtorAging60"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>31–60 Days ($)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            data-testid="input-debtor-aging-60"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="debtorAging90Plus"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>61–90+ Days ($)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            data-testid="input-debtor-aging-90"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="concentrationTopDebtorPercent"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Top Debtor Concentration (%)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            data-testid="input-concentration-top-debtor"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            )}

            {/* ── INSTITUTION fields (shared by shares, managed_funds, term_deposit, bonds) ── */}
            {showInstitution && (
              <div className="space-y-4 border rounded-md p-4">
                <p className="text-sm font-medium text-muted-foreground">Institution Details</p>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="institutionName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Institution Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. ANZ Bank"
                            data-testid="input-institution-name"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {showAccountNumber && (
                    <FormField
                      control={form.control}
                      name="accountNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Account Number</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Account or reference number"
                              data-testid="input-account-number"
                              {...field}
                              value={field.value || ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>
              </div>
            )}

            {/* ── ASX Code (listed shares only) ── */}
            {showAsxCode && (
              <FormField
                control={form.control}
                name="asxCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ASX Code</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. BHP"
                        data-testid="input-asx-code"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* ── UNITS + PRICE (shares and managed funds) ── */}
            {showUnitsPrice && (
              <div className="space-y-4 border rounded-md p-4">
                <p className="text-sm font-medium text-muted-foreground">Holdings</p>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="numberOfUnits"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Number of Units</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.0001"
                            placeholder="0"
                            data-testid="input-number-of-units"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="unitPrice"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Unit Price ($)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.0001"
                            placeholder="0.00"
                            data-testid="input-unit-price"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                {/* Calculated total — display only, not submitted */}
                <div className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2">
                  <span className="text-sm text-muted-foreground">Calculated Total (display only)</span>
                  <span
                    className="text-sm font-semibold"
                    data-testid="calculated-total"
                  >
                    {calcTotal(watchedUnits, watchedPrice)}
                  </span>
                </div>
              </div>
            )}

            {/* ── MATURITY + INTEREST RATE (term deposit, bonds) ── */}
            {showMaturityRate && (
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="maturityDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Maturity Date</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          data-testid="input-maturity-date"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="interestRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Interest Rate (%)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          data-testid="input-interest-rate"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* ── DESCRIPTION (other_financial only) ── */}
            {showOther && (
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Describe the financial asset..."
                        className="min-h-[80px] resize-none"
                        data-testid="textarea-description"
                        rows={3}
                        value={field.value ?? ""}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                data-testid="button-cancel"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending} data-testid="button-save">
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEdit ? "Save Changes" : "Add Financial Asset"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
